import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CollectionsBookmarkOutlinedIcon from '@mui/icons-material/CollectionsBookmarkOutlined';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

import type { UserStandardTreeNode } from '../../utils/userStandardsTree';

import { getCollectionDropData, isTreeItemData } from './dnd/libraryDndData';
import { useLibraryDnd } from './dnd/LibraryDndProvider';

import type { UUID } from '@/shared/types/userStandard';

interface CollectionsPanelProps {
  collections: UserStandardTreeNode[];
  activeCollectionId: UUID | null;
  collapsed: boolean;
  onSelect: (id: UUID | null) => void;
  onNewCollection: () => void;
  /** When provided, a collapse control is shown (desktop only). */
  onToggleCollapse?: () => void;
}

/** Count standards (leaves) anywhere under a collection — a lightweight "scale" hint. */
function countStandards(node: UserStandardTreeNode): number {
  let total = 0;
  for (const child of node.children) {
    if (child.node_type === 'standard') total += 1;
    total += countStandards(child);
  }
  return total;
}

function PickerRow({
  icon,
  label,
  count,
  selected,
  onClick,
  dropCollectionId,
}: {
  icon: ReactNode;
  label: string;
  count?: number;
  selected: boolean;
  onClick: () => void;
  /** When set, this row accepts dragged tree nodes and reparents them into that collection. */
  dropCollectionId?: UUID;
}) {
  const { getDescendantIds } = useLibraryDnd();
  const ref = useRef<HTMLDivElement | null>(null);
  const [isOver, setIsOver] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || !dropCollectionId) return;
    return dropTargetForElements({
      element,
      canDrop: ({ source }) =>
        isTreeItemData(source.data) &&
        source.data.nodeId !== dropCollectionId &&
        !getDescendantIds(source.data.nodeId).has(dropCollectionId),
      getData: () => getCollectionDropData(dropCollectionId),
      onDragEnter: () => setIsOver(true),
      onDragLeave: () => setIsOver(false),
      onDrop: () => setIsOver(false),
    });
  }, [dropCollectionId, getDescendantIds]);

  return (
    <Box
      ref={ref}
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.25,
        py: 0.75,
        mx: 0.5,
        borderRadius: 1,
        cursor: 'pointer',
        outline: isOver ? '2px solid' : 'none',
        outlineColor: 'primary.main',
        bgcolor: isOver ? 'action.hover' : selected ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
      }}
    >
      {icon}
      <Typography
        variant="body2"
        noWrap
        sx={{ flex: 1, minWidth: 0, fontWeight: selected ? 600 : 400 }}
      >
        {label}
      </Typography>
      {count != null && count > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ flex: 'none' }}>
          {count}
        </Typography>
      )}
    </Box>
  );
}

export function CollectionsPanel({
  collections,
  activeCollectionId,
  collapsed,
  onSelect,
  onNewCollection,
  onToggleCollapse,
}: CollectionsPanelProps) {
  if (collapsed) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          py: 1,
        }}
      >
        <Tooltip title="Show collections" placement="right">
          <IconButton size="small" onClick={onToggleCollapse} aria-label="Show collections">
            <CollectionsBookmarkOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="New collection" placement="right">
          <IconButton size="small" onClick={onNewCollection} aria-label="New collection">
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1 }}>
          Collections
        </Typography>
        <Tooltip title="New collection">
          <IconButton size="small" onClick={onNewCollection} aria-label="New collection">
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {onToggleCollapse && (
          <Tooltip title="Hide collections">
            <IconButton size="small" onClick={onToggleCollapse} aria-label="Hide collections">
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', py: 0.5 }}>
        {collections.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              No collections yet.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CreateNewFolderOutlinedIcon />}
              onClick={onNewCollection}
            >
              New collection
            </Button>
          </Box>
        ) : (
          <>
            <PickerRow
              icon={
                <LibraryBooksIcon
                  fontSize="small"
                  color={activeCollectionId === null ? 'primary' : 'action'}
                />
              }
              label="All collections"
              selected={activeCollectionId === null}
              onClick={() => onSelect(null)}
            />
            {collections.map((collection) => {
              const selected = activeCollectionId === collection.id;
              return (
                <PickerRow
                  key={collection.id}
                  icon={
                    <FolderOutlinedIcon fontSize="small" color={selected ? 'primary' : 'action'} />
                  }
                  label={collection.title}
                  count={countStandards(collection)}
                  selected={selected}
                  onClick={() => onSelect(collection.id)}
                  dropCollectionId={collection.id}
                />
              );
            })}
          </>
        )}
      </Box>
    </Box>
  );
}

export default CollectionsPanel;
